import { useCallback, useEffect, useState } from 'react';
import { buildMarketComparisonParams, getMarketComparison } from '../../services/marketComparisonService';
import {
  getMarketComparisonApiFeature,
  isMarketComparisonEnabled,
  isMarketComparisonHidden,
  isMarketComparisonOffResponse,
  subscribeMarketComparisonFeature,
} from '../marketComparisonConfig';
import { normalizeMarketComparison } from '../marketComparison';

/** Cuánto vale una respuesta en caché (la API calcula en el momento; no guarda nada). */
export const MARKET_CACHE_TTL_MS = 10 * 60 * 1000;
const MAX_ENTRIES = 50;

/**
 * Caché del módulo por registro + dueño + filtros (+ `revision`, que cambia cuando se
 * edita la unidad). La comparten la tarjeta y, en T2, el modal: abrir el informe no vuelve
 * a pedir. Guarda también los pedidos en curso para no duplicarlos.
 */
const cache = new Map();
const inflight = new Map();
/** 404 sin `code`: la API tiene el flag apagado; no se vuelve a pedir en esta sesión. */
let apiOff = false;

export function marketComparisonCacheKey(query, ownerUserId, revision = '') {
  const params = buildMarketComparisonParams(query, ownerUserId);
  if (!params) return '';
  const ordered = Object.keys(params).sort().map((k) => `${k}=${params[k]}`).join('&');
  return `${ordered}#${revision == null ? '' : String(revision)}`;
}

/** Para tests y para cambiar de sesión / espacio. */
export function clearMarketComparisonCache() {
  cache.clear();
  inflight.clear();
  apiOff = false;
}

function readCached(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.at > MARKET_CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.value;
}

function writeCached(key, value) {
  cache.set(key, { at: Date.now(), value });
  if (cache.size > MAX_ENTRIES) cache.delete(cache.keys().next().value);
}

/**
 * Pide (o reusa) la comparación. Resuelve a `{ status: 'ready'|'hidden'|'error', data }`.
 * No se cancela con el componente: el resultado queda en caché para el próximo montaje.
 */
function loadMarketComparison(key, query, ownerUserId) {
  const cached = readCached(key);
  if (cached) return Promise.resolve(cached);
  if (inflight.has(key)) return inflight.get(key);
  const promise = getMarketComparison(query, { ownerUserId })
    .then((res) => {
      if (res?._httpOk) {
        const data = normalizeMarketComparison(res);
        const value = data ? { status: 'ready', data } : { status: 'error', data: null };
        if (data) writeCached(key, value);
        return value;
      }
      if (isMarketComparisonOffResponse(res)) apiOff = true;
      if (isMarketComparisonHidden(res)) {
        const value = { status: 'hidden', data: null };
        // 401 no se guarda: después de volver a iniciar sesión la tarjeta tiene que pedir.
        if (res?._httpStatus !== 401) writeCached(key, value);
        return value;
      }
      return { status: 'error', data: null, httpStatus: res?._httpStatus ?? null };
    })
    .catch(() => ({ status: 'error', data: null }))
    .finally(() => { inflight.delete(key); });
  inflight.set(key, promise);
  return promise;
}

/**
 * Comparación de mercado de un registro. Pide al montar (al abrir la ficha).
 *
 * @param {{ itemKind: string, recordId: string, ownerUserId?: string|null,
 *   filters?: { radiusM?: number, months?: number, operation?: string },
 *   revision?: string|number, enabled?: boolean }} options
 * @returns {{ status: 'loading'|'ready'|'error'|'hidden', data: object|null, reload: () => void }}
 */
export function useMarketComparison({
  itemKind,
  recordId,
  ownerUserId = null,
  filters = null,
  revision = '',
  enabled = true,
}) {
  // `features.market_comparison` de getmyrecords: `false` esconde sin pedir; `null` (no se
  // leyó todavía) pide igual y el 404 sin `code` queda de respaldo.
  const [apiFeature, setApiFeature] = useState(getMarketComparisonApiFeature);
  useEffect(() => subscribeMarketComparisonFeature(setApiFeature), []);
  const radiusM = filters?.radiusM;
  const months = filters?.months;
  const operation = filters?.operation;
  const active = Boolean(enabled && isMarketComparisonEnabled() && recordId && apiFeature !== false);
  const key = active
    ? marketComparisonCacheKey({ itemKind, id: recordId, radiusM, months, operation }, ownerUserId, revision)
    : '';
  const [state, setState] = useState(() => {
    if (!key || apiOff) return { key, status: 'hidden', data: null };
    return { key, ...(readCached(key) || { status: 'loading', data: null }) };
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key || apiOff) {
      setState({ key, status: 'hidden', data: null });
      return undefined;
    }
    let cancelled = false;
    const cached = readCached(key);
    if (cached) {
      setState({ key, ...cached });
      return undefined;
    }
    setState({ key, status: 'loading', data: null });
    loadMarketComparison(key, { itemKind, id: recordId, radiusM, months, operation }, ownerUserId)
      .then((value) => {
        if (cancelled) return;
        if (value.status === 'error') {
          console.error('Comparación de mercado: getmarketcomparison falló', value.httpStatus ?? null);
        }
        setState({ key, status: value.status, data: value.data });
      });
    return () => { cancelled = true; };
  }, [key, attempt, itemKind, recordId, radiusM, months, operation, ownerUserId]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // Mientras el efecto no corrió para la clave nueva, no mostrar datos de otra unidad.
  if (state.key !== key) {
    return { status: key && !apiOff ? 'loading' : 'hidden', data: null, reload };
  }
  return { status: state.status, data: state.data, reload };
}
